import { useCallback, useRef, useState } from "react";

export interface ReorderHandlers {
  /** Attach to the drag handle of row `index`. */
  onPointerDown: (e: React.PointerEvent) => void;
}

interface Options {
  /** Number of draggable rows. */
  count: number;
  /** Called once on drop with the final index mapping. */
  onDrop: (from: number, to: number) => void;
  /** Row height in px, used to translate pointer travel into index steps. */
  rowHeight?: number;
}

export interface ReorderState {
  /** Index being dragged, or null. */
  from: number | null;
  /** Index it would land on. */
  to: number | null;
  /** Live pixel offset of the dragged row. */
  offset: number;
}

/**
 * Minimal pointer-based list reordering: press the handle, move vertically,
 * release. Pointer events cover mouse and touch alike, and setPointerCapture
 * keeps the gesture even when the finger leaves the handle. Vertical page
 * scroll is suppressed only while a drag is active (touch-action on the handle).
 */
export function useReorder({ count, onDrop, rowHeight = 56 }: Options) {
  const [state, setState] = useState<ReorderState>({ from: null, to: null, offset: 0 });
  const startY = useRef(0);

  const handlersFor = useCallback(
    (index: number): ReorderHandlers => ({
      onPointerDown: (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        target.setPointerCapture(e.pointerId);
        startY.current = e.clientY;
        setState({ from: index, to: index, offset: 0 });

        const move = (ev: PointerEvent) => {
          const offset = ev.clientY - startY.current;
          const steps = Math.round(offset / rowHeight);
          const to = Math.max(0, Math.min(count - 1, index + steps));
          setState({ from: index, to, offset });
        };
        const up = () => {
          target.releasePointerCapture(e.pointerId);
          target.removeEventListener("pointermove", move);
          target.removeEventListener("pointerup", up);
          target.removeEventListener("pointercancel", up);
          setState((s) => {
            if (s.from !== null && s.to !== null && s.from !== s.to) onDrop(s.from, s.to);
            return { from: null, to: null, offset: 0 };
          });
        };
        target.addEventListener("pointermove", move);
        target.addEventListener("pointerup", up);
        target.addEventListener("pointercancel", up);
      },
    }),
    [count, onDrop, rowHeight],
  );

  return { state, handlersFor };
}

/** Move an item within an array, returning a new array. */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}
